// src/pages/Dashboard.js
import React, { useState,useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import "./Dashboard.css";

import { collection, addDoc,updateDoc,doc, serverTimestamp, query, where, getDocs, limit, increment } from "firebase/firestore";
import { db } from "../services/firebase";

import { useLocation, useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import InstallButton from "../components/InstallButton";


// ─── Helper: map raw API response → normalised shape ─────────
function normaliseResponse(raw) {
  const insights = raw?.data?.insights || {};
  return {
    actions:       insights.actions    || [],
    ideas:         insights.ideas      || [],
    pinComment:    insights.pinComment || "",
    replies:       insights.replies    || [],
    missed:        insights.missed     || [],
    commentsCount: raw?.data?.commentsCount || 0,
    source:        raw?.source || "",
    remaining:     raw?.remaining ?? null,
    limit:         raw?.limit    ?? null,
  };
}

const ACTION_ICONS = ["💬", "🎬", "📌", "🚨", "✍️"];

export default function Dashboard() {
  const { userData, getToken,user } = useAuth();
  const [videoUrl, setVideoUrl] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [videoMeta, setVideoMeta] = useState(null);

  const plan = userData?.plan || "free";
  

  
  const navigate           = useNavigate();
  const [searchParams] = useSearchParams();
  const isViewMode = !!searchParams.get("videoid");

  useEffect(() => {
    const videoId = searchParams.get("videoid");
    if (!videoId || !user) return;

    const loadFromFirestore = async () => {
      setLoading(true);
      try {
        const q = query(
          collection(db, "analyses"),
          where("uid", "==", user.uid),
          where("videoId", "==", videoId),
          limit(1)
        );
        const snap = await getDocs(q);
        if (snap.empty) { setLoading(false); return; }

        const data = snap.docs[0].data();
        setVideoUrl(`https://www.youtube.com/watch?v=${videoId}`);
        setVideoMeta({
          title:   data.videoTitle || videoId,
          thumb:   data.videoThumb || `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
          channel: data.channel || "",
        });
        setAnalysis({
          actions:       data.insights?.actions    || [],
          ideas:         data.insights?.ideas      || [],
          pinComment:    data.insights?.pinComment || "",
          replies:       data.insights?.replies    || [],
          missed:        data.insights?.missed     || [],
          commentsCount: data.commentsCount        || 0,
          source:        data.source               || "",
          remaining:     null,
          limit:         null,
        });
      } catch (err) {
        setError("Failed to load saved analysis.");
      } finally {
        setLoading(false);
      }
    };

    loadFromFirestore();
  }, [searchParams, user]);


const handleAnalyze = async () => {
    if (!videoUrl.trim()) return;


    

  // ── Reset counters if period has changed ──
  const isProUser = plan === "pro";
  const now = new Date();
  const thisWeek = `${now.getFullYear()}-W${Math.ceil(now.getDate() / 7)}`; // "2026-W2"
  const thisMonth = now.toISOString().slice(0, 7); // "2026-05"

  try {
    const updates = {};

    if (!isProUser && userData?.lastUsedWeek !== thisWeek) {
      // New week → reset daily/weekly counter
      updates.videosUsedToday = 0;
      updates.lastUsedWeek = thisWeek;
    }

    if (isProUser && userData?.lastUsedMonth !== thisMonth) {
      // New month → reset monthly counter
      updates.videosUsedMonth = 0;
      updates.lastUsedMonth = thisMonth;
    }

    if (Object.keys(updates).length > 0) {
      await updateDoc(doc(db, "users", user.uid), updates);
    }
  } catch (err) {
    console.warn("Failed to reset usage:", err);
  }

  // ── NOW check limits (against fresh/reset values) ──
  if (!isProUser) {
    const current = userData?.lastUsedWeek !== thisWeek ? 0 : (userData?.videosUsedToday || 0);
    if (current >= 5) {
      setError("You've reached your 5 videos/week limit. Upgrade to Pro for 100/month.");
      return;
    }
  } else {
    const current = userData?.lastUsedMonth !== thisMonth ? 0 : (userData?.videosUsedMonth || 0);
    if (current >= 100) {
      setError("You've reached your 100 videos/month limit.");
      return;
    }
  }




    setLoading(true);
    setError(null);
    setAnalysis(null);
    try {
      const videoId = new URL(videoUrl).searchParams.get("v");
      if (!videoId) throw new Error("Invalid YouTube URL — couldn't find video ID.");


      setVideoMeta(null);
      // Fetch title + thumbnail (free, no API key)
      const oEmbed = await fetch(
        `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`
      );
      const oData = await oEmbed.json();
      const videoTitle = oData.title || videoId;
      const videoThumb = oData.thumbnail_url || `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
      setVideoMeta({ title: videoTitle, thumb: videoThumb, channel: oData.author_name });



      const token    = await getToken();
      const forceNew = false;
      const res = await fetch(
        "https://us-central1-ytcommentanalyze-test.cloudfunctions.net/analyzeVideo",
        {
          method:  "POST",
          headers: {
            "Content-Type":  "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({ data: { videoId, forceNew } }),
        }
      );
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const raw = await res.json();
      console.log("API response:", raw);
      
      const normalised = normaliseResponse(raw.result);
      setAnalysis(normalised);

      if (normalised.source !== "cache") {
       try {
          const updates = { videosUsedToday: increment(1) };
          if (isProUser) updates.videosUsedMonth = increment(1);
          await updateDoc(doc(db, "users", user.uid), updates);
        } catch (err) {
          console.warn("Failed to update usage count:", err);
        }
      }

      // ── Save to Firestore history ──
      try {

          if (normalised.source === "cache") {
              console.log("Cached result — skipping history save");
            } else {

          const oEmbed = await fetch(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`);
          const oData  = await oEmbed.json();
          const videoTitle = oData.title || videoId;

          await addDoc(collection(db, "analyses"), {
            uid:           user.uid,
            videoId:       videoId,
            videoTitle:    videoTitle,         // see note below
            commentsCount: normalised.commentsCount,
            ideasCount:    normalised.ideas.length,
            repliesCount:  normalised.replies.length,
            source:        normalised.source || "",
            createdAt:     serverTimestamp(),
            insights: {
              actions:    normalised.actions,
              ideas:      normalised.ideas,
              pinComment: normalised.pinComment,
              replies:    normalised.replies,
              missed:     normalised.missed,
            },
          });
        }


        } catch (saveErr) {
          console.warn("History save failed:", saveErr); // non-blocking

      }
      

    } catch (err) {
      console.error("Analysis failed:", err);
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const copy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const CopyBtn = ({ text, id }) => (
    <button
      className={`copy-btn ${copiedId === id ? "copied" : ""}`}
      onClick={() => copy(text, id)}
    >
      {copiedId === id ? "✓ Copied" : "Copy"}
    </button>
  );

  return (
    <div className="page-wrap dashboard">

      {/* ─── URL Input ─── */}
      {!isViewMode && (
      <div className="analyze-bar card fade-up">
        <input
          type="text"
          placeholder="Paste YouTube video URL to analyze comments..."
          value={videoUrl}
          onChange={e => setVideoUrl(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleAnalyze()}
          style={{ flex: 1, fontSize: 15 }}
        />
        <button
          className="btn btn-primary"
          onClick={handleAnalyze}
          disabled={loading || !videoUrl.trim()}
          style={{ flexShrink: 0 }}
        >
          {loading ? <span className="spinner" /> : "⚡ Analyze"}
        </button>
      </div>
      )}

      {/* ─── View mode header ─── */}
      {isViewMode && !loading && analysis && (
        <div className="card analyze-bar fade-up" style={{ justifyContent: "space-between" }}>
          <span style={{ fontSize: 15, color: "var(--text-muted)" }}>
            📊 Viewing saved analysis
          </span>
          <button
            className="btn btn-primary"
           onClick={() => {
              setAnalysis(null);
              setVideoMeta(null);
              setVideoUrl("");
              setError(null);
              navigate("/dashboard");
            }}
            style={{ flexShrink: 0 }}
          >
            ⚡ Analyze New Video
          </button>
        </div>
      )}


      {/* ─── Video Preview ─── */}
      {videoMeta && !loading && (
        <div className="card video-preview-card fade-up">
          <img src={videoMeta.thumb} alt={videoMeta.title} className="video-preview-thumb" />
          <div className="video-preview-info">
            <div className="video-preview-title">{videoMeta.title}</div>
            <div className="video-preview-channel">📺 {videoMeta.channel}</div>
            <a
              href={videoUrl}
              target="_blank"
              rel="noreferrer"
              className="btn btn-ghost"
              style={{ fontSize: 12, padding: "5px 10px", marginTop: 6, display: "inline-block" }}
            >
              Open on YouTube ↗
            </a>
          </div>
        </div>
      )}

      {/* ─── Error ─── */}
      {error && <div className="error-bar fade-up">⚠️ {error}</div>}

      {/* ─── Loading ─── */}
      {loading && (
        <div className="loading-state fade-up">
          {["Fetching comments...", "Running AI analysis...", "Generating insights..."].map((t, i) => (
            <div key={i} className="loading-step">
              <div className="shimmer" style={{ width: 24, height: 24, borderRadius: "50%" }} />
              <span>{t}</span>
            </div>
          ))}
        </div>
      )}

      {/* ─── Results ─── */}
      {analysis && !loading && (
        <div className="analysis-grid fade-up">

          {/* Meta strip */}
          <div className="card meta-strip full-width">
            <div className="meta-item">
              <span className="meta-label">Comments analyzed</span>
              <span className="meta-value">{analysis.commentsCount}</span>
            </div>
            {analysis.remaining !== null && (
              <div className="meta-item">
                <span className="meta-label">Analyses remaining today</span>
                <span className="meta-value">{analysis.remaining} / {analysis.limit}</span>
              </div>
            )}
            {/* {analysis.source === "cache" && (
              <div className="meta-item">
                <span className="badge badge-green">⚡ Cached result</span>
              </div>
            )} */}
          </div>

          {/* What to do next */}
          <div className="card hero-section">
            <div className="section-title">What should I do next?</div>
            <div className="next-actions">
              {analysis.actions.map((actionText, i) => (
                <div key={i} className="next-action">
                  <span className="action-icon">{ACTION_ICONS[i % ACTION_ICONS.length]}</span>
                  <span>{actionText}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Video Ideas */}
          <div className="card">
            <div className="section-title">💡 Video Ideas from Comments</div>
            <div className="ideas-list">
              {analysis.ideas.map((idea, i) => (
                <div key={i} className="idea-item">
                  <div className="idea-header">
                    <strong>{idea.title}</strong>
                    <CopyBtn text={idea.title} id={`idea-${i}`} />
                  </div>
                  <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{idea.reason}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Reply Assistant */}
          <div className="card">
            <div className="section-title">💬 Reply Assistant</div>
            <div className="replies-list">
              {analysis.replies.map((r, i) => (
                <div key={i} className="reply-item">
                  <div className="comment-bubble">
                    <p>"{r.comment}"</p>
                  </div>
                  <div className="reply-bubble">
                    <span className="reply-label">AI Reply</span>
                    <p>{r.reply}</p>
                    <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                      <CopyBtn text={r.reply} id={`reply-${i}`} />
                      <a href={videoUrl}  target="_blank" rel="noreferrer"
                        className="btn btn-ghost" style={{ fontSize: 12, padding: "5px 10px" }}>
                        Open YT ↗
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pin Suggestion */}
          {analysis.pinComment && (
            <div className="card pin-card">
              <div className="section-title">📌 Pin This Comment</div>
              <div className="pin-comment">
                <p>"{analysis.pinComment}"</p>
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <CopyBtn text={analysis.pinComment} id="pin" />
                  <a href={videoUrl}  target="_blank" rel="noreferrer"
                    className="btn btn-ghost" style={{ fontSize: 12, padding: "5px 10px" }}>
                    Open YT ↗
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Missed Opportunities */}
          <div className="card">
            <div className="section-title">🚨 Missed Opportunities</div>
            <div className="missed-list">
              {analysis.missed.map((text, i) => (
                <div key={i} className="missed-item">
                  <div style={{ fontSize: 13, flex: 1 }}>{text}</div>
                  <CopyBtn text={text} id={`missed-${i}`} />
                </div>
              ))}
            </div>
          </div>

        </div>
      )}

      {/* ─── Empty state ─── */}
      {!analysis && !loading && !error && (
        <div className="empty-state fade-up">
          <div style={{ fontSize: 56 }}>🎬</div>
          <h2>Paste a YouTube URL to get started</h2>
          <p>CreatorComment will analyze your comments and tell you exactly what to do next.</p>
          {plan === "free" && (
            <div className="badge badge-accent" style={{ marginTop: 12 }}>Free: 5 videos/week</div>
          )}
        </div>
      )}

      <InstallButton/>

    </div>
  );
}